import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import DriverTabBar from '../../components/driver/DriverTabBar';
import MassIcon from '../../components/driver/MassIcon';
import RoundBackButton from '../../components/common/RoundBackButton';
import OrderHistorySheet from '../../components/driver/OrderHistorySheet';
import { useDriverActiveOrder } from '../../hooks/useDriverActiveOrder';
import { useDriverOrderHistory } from '../../hooks/useDriverOrderHistory';
import { placeName } from '../../utils/orderCardFormat';
import { orderTitle } from '../../utils/orderTitle';
import { isCarrierOrder } from '../../utils/carrierOrder';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckLoader, TruckEmpty } from '../../components/common/truck';

const STATUS_TEXT = {
  matched:           'Klar for henting',
  in_progress:       'Under transport',
  delivered_pending: 'Venter på bekreftelse',
  completed:         'Fullført',
  cancelled:         'Kansellert av bestiller',
};

const kr = n => Number(n).toLocaleString('nb-NO');

// Historikk: pågående, fullførte og kansellerte oppdrag
export default function DriverOrders() {
  const navigate = useNavigate();
  const { activeOrders, loading: loadingActive } = useDriverActiveOrder();
  const { history, cancelled, loading: loadingHistory, error: historyError } = useDriverOrderHistory();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const handleCloseModal = useCallback(() => setSelectedOrder(null), []);

  const loading = loadingActive || loadingHistory;
  const empty = activeOrders.length === 0 && history.length === 0 && cancelled.length === 0;

  return (
    <div className="driver-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <div style={styles.top}>
          <RoundBackButton onClick={() => navigate('/driver/profil')} label="Tilbake til profil" />
        </div>
        <h1 style={styles.heading}>Historikk</h1>

        {loading && <TruckLoader showLabel label="Laster…" />}

        {!loading && historyError && (
          <div role="alert" style={styles.error}>
            Kunne ikke laste oppdragshistorikk. Prøv å laste siden på nytt.
          </div>
        )}

        {!loading && (
          <>
            {activeOrders.length > 0 && (
              <Section title="Pågående">
                {activeOrders.map(order => (
                  <OrderRow key={order.id} order={order} onClick={() => navigate(`/driver/active/${order.id}`)} />
                ))}
              </Section>
            )}

            {history.length > 0 && (
              <Section title="Fullførte">
                {history.map(order => (
                  <OrderRow key={order.id} order={order} onClick={() => setSelectedOrder(order)} />
                ))}
              </Section>
            )}

            {cancelled.length > 0 && (
              <Section title="Kansellerte">
                {cancelled.map(order => (
                  <OrderRow key={order.id} order={order} onClick={() => setSelectedOrder(order)} />
                ))}
              </Section>
            )}

            {empty && (
              <TruckEmpty title="Ingen oppdrag ennå" text="Fullførte og kansellerte oppdrag vises her." />
            )}
          </>
        )}
      </div>

      {selectedOrder && <OrderHistorySheet order={selectedOrder} onClose={handleCloseModal} />}
      <DriverTabBar />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 10 }}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

function OrderRow({ order, onClick }) {
  const date = order.deliveredAt?.toDate?.()
    ?? order.cancelledByOrdererAt?.toDate?.()
    ?? (order.scheduledAt?.toDate?.() ?? null);
  const dateText = date ? date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).toUpperCase() : '';

  return (
    <button type="button" style={styles.row} onClick={onClick}>
      <MassIcon massType={order.massType} />
      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <div style={styles.rowTitle}>{orderTitle(order)}</div>
        <div style={styles.rowSub}>{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}</div>
        <div style={styles.rowMono}>{[dateText, (isCarrierOrder(order) && order.status === 'cancelled' ? 'Kansellert av firmaet' : STATUS_TEXT[order.status])?.toUpperCase()].filter(Boolean).join(' · ')}</div>
      </div>
      {order.driverPayout != null && <div style={styles.payout}>{kr(order.driverPayout)}</div>}
    </button>
  );
}

const styles = {
  root:         { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:         { maxWidth: 560, margin: '0 auto', padding: '20px 20px 24px', width: '100%', flex: 1, paddingTop: 'max(32px, env(safe-area-inset-top))' },
  top:          { marginBottom: 8 },
  heading:      { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, color: COLORS.textDark, margin: '4px 0 10px' },
  state:        { textAlign: 'center', padding: 40, color: COLORS.textLight, fontSize: 14 },
  error:        { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '12px 16px', borderRadius: 12, fontSize: 14, marginBottom: 16 },
  empty:        { textAlign: 'center', padding: '48px 16px' },
  emptyTitle:   { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark },
  emptyText:    { fontSize: 14, color: COLORS.textMid, marginTop: 6 },
  sectionTitle: { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: COLORS.textDark, margin: '14px 0 2px' },
  row:          { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  rowTitle:     { fontWeight: 700, fontSize: 15 },
  rowSub:       { fontSize: 13, color: COLORS.textMid, marginTop: 1 },
  rowMono:      { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 3, letterSpacing: '0.03em' },
  payout:       { fontFamily: FONTS.display, fontWeight: 700, fontSize: 24, lineHeight: 1, flexShrink: 0 },
};
